import { useTranslation } from "react-i18next";
import ParanoidiPricingCards from "../../pricing/ParanoidiPricingCards";
import LanguageSwitcher from "../../shared/components/LanguageSwitcher";

const MATTI_MOCKUP_IMAGE = "/assets/paranoidi/matti-mock-up-paranoidi1.png";
const READER_SCREENSHOT_IMAGE =
  "/assets/paranoidi/paranoidi-demo-elias-lukutila-prompteri.png";

const READER_HIGHLIGHT_IDS = [
  "lyricsChordsNotes",
  "prompter",
  "adjustable",
  "themes",
  "navigation",
  "playedStatus",
  "setlists",
  "printPdf",
  "nextSong",
];

const FEATURE_GROUPS = [
  {
    id: "audience",
    itemIds: [
      "qr",
      "link",
      "search",
      "freeRequest",
      "random",
      "nameMessage",
      "devices",
    ],
  },
  {
    id: "website",
    itemIds: [
      "nameIntro",
      "hero",
      "websiteContact",
      "socialMusic",
      "whatsapp",
      "faq",
      "qrAssets",
    ],
  },
  {
    id: "management",
    itemIds: [
      "toggle",
      "hide",
      "statuses",
      "popular",
      "raffle",
      "freeOrLimited",
    ],
  },
  {
    id: "library",
    itemIds: [
      "manualBulk",
      "pdfDocx",
      "songbookChordpro",
      "metadata",
      "occasionTags",
      "lyricsChords",
    ],
  },
  {
    id: "plus",
    itemIds: [
      "events",
      "eventLimit",
      "generator",
      "saved",
      "readerPrompter",
    ],
  },
  {
    id: "memory",
    itemIds: [
      "notes",
      "banter",
      "songNotes",
      "stats",
      "reports",
      "partners",
    ],
  },
];

const PROCESS_STEP_IDS = [
  "performerDetails",
  "importLibrary",
  "shareQr",
  "collect",
  "plusTools",
];

const PRINT_PRODUCT_ITEM_IDS = [
  "performerName",
  "qr",
  "logo",
  "products",
];

function FeatureCard({ group, index, isEnglish }) {
  return (
    <article
      className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 shadow-2xl shadow-black/20 transition duration-500 hover:-translate-y-1 hover:border-red-400/30 hover:bg-white/[0.055] sm:p-8"
      style={{
        animation: `paranoidiFadeUp 700ms ease-out ${index * 90}ms both`,
      }}
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-red-500/10 blur-3xl transition duration-700 group-hover:bg-red-500/20" />

      <p className="relative text-xs font-black uppercase tracking-[0.2em] text-red-400">
        {group.eyebrow}
      </p>

      <h3 className="relative mt-3 text-2xl font-black uppercase tracking-[-0.04em] text-white sm:text-3xl">
        {group.title}
      </h3>

      <p className="relative mt-4 text-sm font-semibold leading-7 text-zinc-400">
        {group.text}
      </p>

      <ul className="relative mt-6 grid gap-3">
        {group.items.map((item) => (
          <li
            key={item.id}
            className="flex gap-3 rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm font-bold text-zinc-200 transition duration-300 group-hover:border-white/15"
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs text-white shadow-lg shadow-red-950/40">
              ✓
            </span>

            <span>{item.label}</span>
          </li>
        ))}
      </ul>

      {isEnglish && group.id === "audience" && (
        <div className="relative mt-6">
          <a
            href="/en/song-requests"
            className="inline-flex items-center justify-center rounded-full border border-red-400/30 bg-red-500/10 px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-red-200 transition hover:-translate-y-0.5 hover:border-red-400/60 hover:bg-red-500/20 hover:text-white"
          >
            Explore the song request app
          </a>
        </div>
      )}
    </article>
  );
}

function ProcessStep({ step, index }) {
  return (
    <div
      className="group grid gap-4 rounded-[1.5rem] border border-white/10 bg-black/35 p-5 transition duration-500 hover:-translate-y-1 hover:border-red-400/30 hover:bg-red-500/[0.06] sm:grid-cols-[3rem_1fr] sm:items-center"
      style={{
        animation: `paranoidiFadeUp 700ms ease-out ${index * 90}ms both`,
      }}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500 text-sm font-black text-white shadow-lg shadow-red-950/40 transition duration-500 group-hover:scale-110">
        {index + 1}
      </span>

      <div>
        <h3 className="text-base font-black uppercase tracking-[-0.02em] text-white">
          {step.title}
        </h3>

        <p className="mt-1 text-sm font-semibold leading-6 text-zinc-400">
          {step.text}
        </p>
      </div>
    </div>
  );
}

export default function FeaturesPage() {
  const { t, i18n } = useTranslation("admin");
  const isEnglish = i18n.resolvedLanguage?.toLowerCase().startsWith("en");

  const readerHighlights = READER_HIGHLIGHT_IDS.map((id) => ({
    id,
    label: t(`featuresPage.reader.highlights.${id}`),
  }));

  const featureGroups = FEATURE_GROUPS.map((group) => ({
    id: group.id,
    eyebrow: t(`featuresPage.features.groups.${group.id}.eyebrow`),
    title: t(`featuresPage.features.groups.${group.id}.title`),
    text: t(`featuresPage.features.groups.${group.id}.text`),
    items: group.itemIds.map((itemId) => ({
      id: itemId,
      label: t(`featuresPage.features.groups.${group.id}.items.${itemId}`),
    })),
  }));

  const processSteps = PROCESS_STEP_IDS.map((id) => ({
    id,
    title: t(`featuresPage.process.steps.${id}.title`),
    text: t(`featuresPage.process.steps.${id}.text`),
  }));

  const printProductItems = PRINT_PRODUCT_ITEM_IDS.map((id) => ({
    id,
    label: t(`featuresPage.printProducts.items.${id}`),
  }));

  return (
    <main className="min-h-screen overflow-hidden bg-[#050505] text-white">
      <style>
        {`
          @keyframes paranoidiFadeUp {
            0% {
              opacity: 0;
              transform: translateY(28px);
            }

            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes paranoidiFloat {
            0% {
              transform: translate3d(0, 0, 0) rotate(-1deg);
            }

            50% {
              transform: translate3d(0, -18px, 0) rotate(1deg);
            }

            100% {
              transform: translate3d(0, 0, 0) rotate(-1deg);
            }
          }

          @keyframes paranoidiPulseGlow {
            0%, 100% {
              opacity: 0.42;
              transform: scale(1);
            }

            50% {
              opacity: 0.9;
              transform: scale(1.08);
            }
          }

          .paranoidi-features-language {
            gap: 0 !important;
          }

          .paranoidi-features-language > span:first-child {
            display: none;
          }
        `}
      </style>

      <section id="tuote" className="relative overflow-hidden border-b border-white/10">
        <div className="theme-dark absolute right-4 top-4 z-30 sm:right-6 sm:top-6 lg:right-10 lg:top-8">
          <LanguageSwitcher
            variant="public"
            className="paranoidi-features-language !mt-0"
          />
        </div>

        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.32),transparent_32rem),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_30rem)]" />

        <div className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl [animation:paranoidiPulseGlow_5s_ease-in-out_infinite]" />

        <div className="relative mx-auto grid max-w-[96rem] gap-12 px-5 pt-20 pb-6 sm:px-8 sm:pt-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:px-10 lg:pt-14 lg:pb-16">
          <div className="max-w-4xl">
            <h1 className="text-[2.35rem] font-black uppercase leading-[0.9] tracking-[-0.065em] text-white sm:text-6xl sm:tracking-[-0.075em] lg:text-7xl">
              {t("featuresPage.hero.title")}
            </h1>

            <p className="mt-6 max-w-3xl text-lg font-semibold leading-8 text-zinc-300 sm:text-xl sm:leading-9">
              {t("featuresPage.hero.text")}
            </p>

            <p className="mt-4 max-w-3xl text-base font-semibold leading-8 text-zinc-400 sm:text-lg sm:leading-8">
              {t("featuresPage.hero.secondaryText")}
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white shadow-lg shadow-red-950/30 transition hover:-translate-y-0.5 hover:bg-red-400"
              >
                {t("featuresPage.hero.button")}
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-0 rounded-[3rem] bg-red-500/10 blur-3xl" />

            <img
              src={MATTI_MOCKUP_IMAGE}
              alt={t("featuresPage.hero.imageAlt")}
              className="relative mx-auto w-full max-w-5xl object-contain drop-shadow-[0_30px_80px_rgba(0,0,0,0.65)] [animation:paranoidiFloat_7s_ease-in-out_infinite]"
            />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-red-400/20 bg-red-500/[0.06]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.22),transparent_32rem)]" />

        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
            {t("featuresPage.boost.eyebrow")}
          </p>

          <div className="mt-4 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <h2 className="text-4xl font-black uppercase leading-[0.95] tracking-[-0.06em] text-white sm:text-5xl">
                {t("featuresPage.boost.title")}
              </h2>

              <p className="mt-5 max-w-2xl text-base font-semibold leading-8 text-zinc-300">
                {t("featuresPage.boost.text")}
              </p>

              <p className="mt-3 max-w-2xl text-sm font-semibold leading-7 text-zinc-400">
                {t("featuresPage.boost.note")}
              </p>
            </div>

            <div className="rounded-[2rem] border border-emerald-400/20 bg-emerald-500/10 p-6 sm:p-8">
              <p className="text-xl font-black text-white">
                {t("featuresPage.boost.stripeTitle")}
              </p>

              <p className="mt-3 text-sm font-semibold leading-7 text-zinc-300">
                {t("featuresPage.boost.stripeText")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative border-y border-white/10 bg-white/[0.025]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.16),transparent_34rem)]" />

        <div className="relative mx-auto grid max-w-[96rem] gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:px-10 lg:py-24">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              {t("featuresPage.reader.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase leading-[0.95] tracking-[-0.06em] text-white sm:text-5xl">
              {t("featuresPage.reader.title")}
            </h2>

            <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
              {t("featuresPage.reader.text")}
            </p>

            <div className="mt-7 grid gap-3">
              {readerHighlights.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm font-bold text-zinc-200"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                    ✓
                  </span>

                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            {isEnglish && (
              <div className="mt-7">
                <a
                  href="/en/setlist-app-for-musicians"
                  className="inline-flex items-center justify-center rounded-full border border-red-400/30 bg-red-500/10 px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-red-200 transition hover:-translate-y-0.5 hover:border-red-400/60 hover:bg-red-500/20 hover:text-white"
                >
                  Explore the setlist app for musicians
                </a>
              </div>
            )}
          </div>

          <div className="relative mx-auto w-full max-w-[34rem] overflow-hidden rounded-[2.5rem] border border-white/10 bg-black/50 p-3 shadow-2xl shadow-black/40 sm:p-4 lg:max-w-[30rem] xl:max-w-[34rem]">
            <div className="h-[28rem] overflow-hidden rounded-[2rem] sm:h-[32rem] lg:h-[34rem]">
              <img
                src={READER_SCREENSHOT_IMAGE}
                alt={t("featuresPage.reader.imageAlt")}
                className="h-full w-full object-cover object-top shadow-2xl shadow-black/40"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        id="ominaisuudet"
        className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20"
      >
        <div className="max-w-3xl">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
            {t("featuresPage.features.eyebrow")}
          </p>

          <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] sm:text-5xl">
            {t("featuresPage.features.title")}
          </h2>

          <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
            {t("featuresPage.features.text")}
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2">
          {featureGroups.map((group, index) => (
            <FeatureCard
              key={group.id}
              group={group}
              index={index}
              isEnglish={isEnglish}
            />
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-white/10 bg-white/[0.025]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_right,rgba(239,68,68,0.18),transparent_32rem)]" />

        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-10 lg:py-20">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              {t("featuresPage.printProducts.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase leading-[0.95] tracking-[-0.06em] text-white sm:text-5xl">
              {t("featuresPage.printProducts.title")}
            </h2>

            <p className="mt-5 max-w-2xl text-base font-semibold leading-8 text-zinc-300">
              {t("featuresPage.printProducts.text")}
            </p>

            <p className="mt-3 max-w-2xl text-sm font-semibold leading-7 text-zinc-400">
              {t("featuresPage.printProducts.note")}
            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {printProductItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm font-bold text-zinc-200"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                    ✓
                  </span>

                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <a
                href="/taydenna-tilaustasi"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white shadow-lg shadow-red-950/30 transition hover:-translate-y-0.5 hover:bg-red-400"
              >
                {t("featuresPage.printProducts.button")}
              </a>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-2xl">
            <div className="pointer-events-none absolute inset-8 rounded-full bg-red-500/15 blur-3xl" />

            <img
              src="/assets/print-products/paranoidi-matti-oksa-tuopinaluset.webp"
              alt={t("featuresPage.printProducts.imageAlt")}
              className="relative mx-auto w-full object-contain drop-shadow-[0_30px_70px_rgba(0,0,0,0.55)]"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.025]">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
                {t("featuresPage.process.eyebrow")}
              </p>

              <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] sm:text-5xl">
                {t("featuresPage.process.title")}
              </h2>
            </div>

            <div className="grid gap-4">
              {processSteps.map((step, index) => (
                <ProcessStep key={step.id} step={step} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
        <div className="pointer-events-none absolute -right-24 top-20 h-80 w-80 rounded-full bg-red-500/10 blur-3xl" />

        <div className="max-w-3xl">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
            {t("featuresPage.plans.eyebrow")}
          </p>

          <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] sm:text-5xl">
            {t("featuresPage.plans.title")}
          </h2>

          <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
            {t("featuresPage.plans.text")}
          </p>
        </div>

        <div className="relative mt-10">
          <ParanoidiPricingCards showExcluded={true} />
        </div>

        <div className="relative mt-10 overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 text-center shadow-2xl shadow-black/25 sm:p-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.18),transparent_26rem)]" />

          <div className="relative">
            <h2 className="text-3xl font-black uppercase tracking-[-0.05em] text-white">
              {t("featuresPage.plans.ctaTitle")}
            </h2>

            <div className="mt-7">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white shadow-lg shadow-red-950/30 transition hover:-translate-y-0.5 hover:bg-red-400"
              >
                {t("featuresPage.plans.ctaButton")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
